import * as React from "react";

import { cn } from "../cn";
import { CountPill } from "./count-pill";

// Design spec §5 and §8.2: the Home layout. `HomeGrid` is a 1200 px grid with a 320 px aside
// from 1024 up (one column below), inside the shell's page frame (which pads it); `HomeCard` is a hairline card (radius 11) with a 44 px header
// (15 / 600 title, optional count pill, actions on the right) over a body that holds rows edge
// to edge (`flush`) or padded 16. `HomeCardRow` is one 44 px row on a --border-subtle rule.

export type HomeGridProps = React.ComponentProps<"div"> & {
  /** The right column (Focus, Recent, Add-ons). */
  aside?: React.ReactNode;
  /** The full-width row above both columns (greeting). */
  header?: React.ReactNode;
};

export function HomeGrid({ aside, header, className, children, ...props }: HomeGridProps) {
  return (
    <div
      data-slot="home-grid"
      className={cn(
        "grid w-full max-w-[1200px] gap-x-8 gap-y-6 lg:grid-cols-[minmax(0,1fr)_320px]",
        className,
      )}
      {...props}
    >
      {header ? <div className="min-w-0 lg:col-span-2">{header}</div> : null}
      <div className="flex min-w-0 flex-col gap-6">{children}</div>
      {aside ? (
        <aside aria-label="Overview" className="flex min-w-0 flex-col gap-6">
          {aside}
        </aside>
      ) : null}
    </div>
  );
}

/**
 * HOME-DASH: `compact` cards inside a widget grid — a 36 px header with a 13 px title and 36 px
 * rows — so a dashboard shows more and wastes less. Set once around the grid.
 */
export const HomeCardDensity = React.createContext<"default" | "compact">("default");

export type HomeCardProps = Omit<React.ComponentProps<"section">, "title"> & {
  title: React.ReactNode;
  /** Shown once known (`undefined` while loading). */
  count?: number | undefined;
  /** Header actions on the right (a link or a small button). */
  action?: React.ReactNode;
  /** Body without padding: for `HomeCardRow` lists. */
  flush?: boolean;
};

export function HomeCard({
  title,
  count,
  action,
  flush = false,
  className,
  children,
  ...props
}: HomeCardProps) {
  const headingId = React.useId();
  const compact = React.useContext(HomeCardDensity) === "compact";
  return (
    <section
      data-slot="home-card"
      data-density={compact ? "compact" : undefined}
      aria-labelledby={headingId}
      className={cn(
        "min-w-0 overflow-hidden rounded-lg border border-border bg-card text-card-foreground",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex items-center gap-2 border-b border-subtle",
          compact ? "h-9 px-3" : "h-11 px-4",
        )}
      >
        <h2
          id={headingId}
          className={cn(
            "min-w-0 truncate font-heading font-semibold text-foreground",
            compact ? "text-[13px] leading-5" : "text-[15px] leading-[22px]",
          )}
        >
          {title}
        </h2>
        {count !== undefined ? <CountPill count={count} /> : null}
        {action ? <div className="ml-auto flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>
      <div data-slot="home-card-body" className={cn(!flush && (compact ? "p-3" : "p-4"))}>
        {children}
      </div>
    </section>
  );
}

export type HomeCardRowProps = Omit<React.ComponentProps<"div">, "title"> & {
  /** 16–24 px lead (avatar, icon, dot). */
  lead?: React.ReactNode;
  title: React.ReactNode;
  /** 12 px muted line under the title. */
  description?: React.ReactNode;
  /** Right-aligned badges, values or buttons. */
  trailing?: React.ReactNode;
};

export function HomeCardRow({
  lead,
  title,
  description,
  trailing,
  className,
  ...props
}: HomeCardRowProps) {
  const compact = React.useContext(HomeCardDensity) === "compact";
  return (
    <div
      data-slot="home-card-row"
      className={cn(
        "flex items-center gap-3 border-t border-subtle first:border-t-0",
        compact ? "min-h-9 px-3 py-1" : "min-h-11 px-4 py-2",
        className,
      )}
      {...props}
    >
      {lead !== undefined ? <span className="flex shrink-0 items-center">{lead}</span> : null}
      <div className="min-w-0 flex-1">
        <div className="min-w-0 truncate text-sm text-foreground">{title}</div>
        {description ? (
          <div className="text-xs leading-[1.55] text-muted-foreground">{description}</div>
        ) : null}
      </div>
      {trailing ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 text-xs text-muted-foreground tabular-nums">
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
